<script generic="TModelValue extends string | number" lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';

interface Props {
  path: string;
  label?: string;
  value?: TModelValue;
  readonly?: boolean;
  disabled?: boolean;
  isHtml?: boolean;
  subLabel?: string;
  hasError?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  styleAsField?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  styleAsField: false,
});

const emit = defineEmits<{
  blur: [];
  click: [event: MouseEvent];
}>();

const model = defineModel<boolean | TModelValue[]>();

const checkValue = () => {
  if (Array.isArray(model.value)) {
    if (props.value === undefined) {
      return false;
    }
    return model.value.includes(props.value);
  }

  return model.value;
};
</script>

<template>
  <div :class="['flex min-h-10 items-center', { error: hasError }]" @click="emit('click', $event)">
    <div v-if="!readonly" class="flex grow items-baseline">
      <label
        :for="props.path"
        class="mb-0 flex items-center rounded-lg"
        :class="{
          'w-full': props.fullWidth,
          'border-red': props.hasError,
          'border-grey-250': !props.hasError,
          'cursor-pointer': !props.disabled,
          'cursor-default': props.disabled,
          'border px-4 py-2.5': props.styleAsField,
          'bg-grey-50': props.styleAsField && props.disabled,
        }"
        data-testid="checkbox-label"
      >
        <span
          :class="[
            'font-inherit relative grid size-[1.125rem] max-w-[1.125rem] flex-[1_0_auto] place-content-center border text-current focus-within:border-primary-600 focus-within:has-[:checked]:bg-primary-600',
            props.disabled
              ? 'cursor-not-allowed border-grey-200 bg-grey-200 text-grey'
              : checkValue()
                ? 'cursor-pointer border-primary bg-primary text-white'
                : props.hasError
                  ? 'cursor-pointer border-red'
                  : 'cursor-pointer border-grey-400',
          ]"
        >
          <input
            type="checkbox"
            :id="props.path"
            v-model="model"
            :value="props.value"
            @blur="emit('blur')"
            :disabled="props.disabled"
            :required="props.required"
            data-testid="checkbox-input"
            class="absolute inset-0 z-10 size-full opacity-0"
            :class="[props.disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
          />

          <FontAwesomeIcon
            v-show="checkValue()"
            :icon="['fas', 'check']"
            :class="['text-xs', props.disabled ? 'text-grey' : 'text-white']"
          />
        </span>

        <span class="select-none" :class="{ 'ml-2': !!props.subLabel }">
          {{ props.subLabel }}
        </span>
      </label>
    </div>
    <div v-else>
      <span class="icon-text">
        <span class="icon">
          <FontAwesomeIcon v-if="checkValue()" :icon="['fas', 'check']" class="text-success" />
          <FontAwesomeIcon v-else :icon="['fas', 'times']" class="text-red" />
        </span>
        <span data-testid="checkbox-label-readonly" :class="{ 'ml-2': !!props.subLabel }">
          {{ props.subLabel }}
        </span>
      </span>
    </div>
  </div>
</template>
